import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronRight,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { BrandCopy } from "../components/BrandWordmarkText";
import { init as initAnalytics, track } from "../lib/analytics";
import { MarketingTopBar, SiteFooter, requestBookDemo } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { useMarketingContent } from "../lib/marketingContent";
import MarketingSeo from "../components/marketing/MarketingSeo";
import MarketingNotFound from "../components/marketing/MarketingNotFound";
import ButtonLink from "../components/marketing/ButtonLink";
import { Badge, Button, Card } from "../components/ui/primitives";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";
import {
  SOLUTION_KEYS,
  SOLUTION_REGISTRY,
  solutionFaqSchema,
  solutionFor,
} from "./solutions/solutionRegistry";
import SolutionProductShowcase from "./solutions/SolutionProductShowcase";
import { SolutionReveal, SolutionStagger, SolutionStaggerItem } from "./solutions/SolutionMotion";
import "./solutions/solution-pages.css";

function SolutionHeroMedia({ media, priority = false }) {
  const avif = media.widths.map((width) => `${media.base}.w${width}.avif ${width}w`).join(", ");
  const webp = media.widths.map((width) => `${media.base}.w${width}.webp ${width}w`).join(", ");
  const fallbackWidth = media.widths.find((width) => width >= 960) || media.widths.at(-1);
  const sizes = "(max-width: 640px) calc(100vw - 28px), (max-width: 1336px) calc(100vw - 56px), 1280px";
  return (
    <figure className="solution-hero-media">
      <picture>
        <source type="image/avif" srcSet={avif} sizes={sizes} />
        <source type="image/webp" srcSet={webp} sizes={sizes} />
        <img
          alt={media.alt}
          decoding="async"
          height={media.height}
          loading={priority ? "eager" : "lazy"}
          src={`${media.base}.w${fallbackWidth}.webp`}
          width={media.width}
        />
      </picture>
    </figure>
  );
}

function SolutionSectionNav() {
  const items = React.useMemo(() => [
    ["solution-evidence", "Inside the product"],
    ["solution-workflows", "Workflows"],
    ["solution-capabilities", "Capabilities"],
    ["solution-fit", "Fit & boundaries"],
    ["solution-rollout", "Rollout"],
    ["solution-faq", "FAQ"],
  ], []);
  const [activeId, setActiveId] = React.useState(items[0][0]);
  const linkRefs = React.useRef(new Map());
  React.useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible?.target?.id) setActiveId(visible.target.id);
    }, { rootMargin: "-22% 0px -64%", threshold: [0, 0.12, 0.45] });
    items.forEach(([id]) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, [items]);
  React.useEffect(() => {
    linkRefs.current.get(activeId)?.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: "auto" });
  }, [activeId]);
  const focusSection = (id) => {
    setActiveId(id);
    window.requestAnimationFrame(() => document.getElementById(id)?.focus({ preventScroll: true }));
  };
  return (
    <nav className="solution-section-nav" aria-label="Sections on this solution page">
      <span>Explore this solution</span>
      <ul>
        {items.map(([id, label], index) => (
          <li key={id}>
            <a
              href={`#${id}`}
              aria-current={activeId === id ? "location" : undefined}
              onClick={() => focusSection(id)}
              ref={(node) => {
                if (node) linkRefs.current.set(id, node);
                else linkRefs.current.delete(id);
              }}
            >
              <b aria-hidden="true">{String(index + 1).padStart(2, "0")}</b>
              <span>{label}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function SectionHeading({ eyebrow, id, title, description }) {
  return (
    <div className="solution-section-heading">
      <p>{eyebrow}</p>
      <h2 id={id}><BrandCopy>{title}</BrandCopy></h2>
      {description ? <div><BrandCopy>{description}</BrandCopy></div> : null}
    </div>
  );
}

function SolutionWorkflowSection({ solution }) {
  return (
    <section className="solution-section" id="solution-workflows" tabIndex="-1" aria-labelledby="solution-workflows-title">
      <SectionHeading
        eyebrow="A connected operating loop"
        id="solution-workflows-title"
        title={solution.workflowHeading}
        description={solution.workflowIntro}
      />
      <ol className="solution-workflow-grid">
        {solution.workflows.map((item, index) => (
          <Card as="li" className="solution-workflow-card" key={item.title}>
            <div className="solution-workflow-card__top">
              <span className="solution-workflow-card__number">{String(index + 1).padStart(2, "0")}</span>
              <span className="solution-workflow-card__icon"><item.icon aria-hidden="true" /></span>
            </div>
            <p className="solution-workflow-card__eyebrow">{item.eyebrow}</p>
            <h3>{item.title}</h3>
            <p>{item.description}</p>
            <div className="solution-workflow-card__evidence">
              <Check aria-hidden="true" />
              <span>{item.evidence}</span>
            </div>
          </Card>
        ))}
      </ol>
    </section>
  );
}

function SolutionCapabilitySection({ solution }) {
  return (
    <section className="solution-section" id="solution-capabilities" tabIndex="-1" aria-labelledby="solution-capabilities-title">
      <SectionHeading
        eyebrow="Capability map"
        id="solution-capabilities-title"
        title={`Build the operating system ${solution.audience} need every day.`}
        description="Open any capability for the detailed workflow, then shape the final scope around your roles, providers and rollout plan."
      />
      <SolutionStagger as="div" className="solution-capability-grid" stagger={0.045} amount={0.1}>
        {solution.modules.map((item) => (
          <SolutionStaggerItem as="div" key={item.title}>
            <Card as="article" className="solution-capability-card">
              <div className="solution-capability-card__top">
                <span><item.icon aria-hidden="true" /></span>
                <Badge tone={item.availability === "Available now" ? "blue" : "neutral"}>{item.availability}</Badge>
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <Link to={`/modules/${item.slug}`} aria-label={`Explore ${item.title}`}>
                Explore capability <ArrowRight aria-hidden="true" />
              </Link>
            </Card>
          </SolutionStaggerItem>
        ))}
      </SolutionStagger>
      <p className="solution-capability-more">
        Looking for the complete inventory? <Link to="/modules">Browse 50+ modules across 9 workspaces</Link>.
      </p>
    </section>
  );
}

function SolutionFitSection({ solution }) {
  return (
    <section className="solution-section" id="solution-fit" tabIndex="-1" aria-labelledby="solution-fit-title">
      <SectionHeading
        eyebrow="A clear-fit plan"
        id="solution-fit-title"
        title="Set the operating boundaries once—then give every team a confident path."
      />
      <div className="solution-fit-grid">
        {solution.fit.map((item, index) => (
          <Card as="article" className="solution-fit-card" key={item.label}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h3>{item.label}</h3>
            <p>{item.value}</p>
          </Card>
        ))}
      </div>
      <div className="solution-ai-migration">
        <Card as="article" className="solution-ai-migration__card solution-ai-migration__card--ai">
          <span><Sparkles aria-hidden="true" /></span>
          <div>
            <p>Aira AI</p>
            <h3>Permission-scoped answers in 11 languages</h3>
            <p>Ask supported questions about records the person is already allowed to see. The source record and permissions remain authoritative.</p>
            <Link to="/aira">See the governed AI suite <ArrowRight aria-hidden="true" /></Link>
          </div>
        </Card>
        <Card as="article" className="solution-ai-migration__card">
          <span><ShieldCheck aria-hidden="true" /></span>
          <div>
            <p>Switching</p>
            <h3>Review mappings before committing supported student data</h3>
            <p>The student import flow maps CSV/XLSX headers, surfaces row-level validation and supports a dry run before commit.</p>
            <Link to="/switch">See how switching works <ArrowRight aria-hidden="true" /></Link>
          </div>
        </Card>
      </div>
    </section>
  );
}

function SolutionRolloutSection({ solution }) {
  return (
    <section className="solution-section" id="solution-rollout" tabIndex="-1" aria-labelledby="solution-rollout-title">
      <SectionHeading
        eyebrow="Designed for adoption"
        id="solution-rollout-title"
        title="Move from the first mapped workflow to daily confidence in four stages."
        description="Each stage has a clear owner, a visible outcome and an acceptance point before the next part of the institution comes online."
      />
      <ol className="solution-rollout-list">
        {solution.rollout.map((item, index) => (
          <Card as="li" className="solution-rollout-card" key={item.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><h3>{item.title}</h3><p>{item.description}</p></div>
          </Card>
        ))}
      </ol>
      <Card className="solution-rollout-cta">
        <div>
          <p>Bring your current process</p>
          <h3>Use the demo to review a real workflow, not a generic slide deck.</h3>
        </div>
        <Button
          variant="secondary"
          onClick={() => requestBookDemo({ placement: "solution_rollout", contextLabel: `${solution.audience} workflows` })}
        >
          Book a workflow review <ArrowRight aria-hidden="true" />
        </Button>
      </Card>
    </section>
  );
}

function SolutionFaqSection({ solution }) {
  return (
    <section className="solution-section" id="solution-faq" tabIndex="-1" aria-labelledby="solution-faq-title">
      <SectionHeading
        eyebrow="Plan with confidence"
        id="solution-faq-title"
        title={`The questions ${solution.audience} ask before a confident rollout.`}
      />
      <div className="solution-faq-list">
        {solution.faqs.map((item, index) => (
          <Card as="details" className="solution-faq-card" key={item.question}>
            <summary>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <span className="solution-faq-card__question">{item.question}</span>
              <b aria-hidden="true">+</b>
            </summary>
            <p>{item.answer}</p>
          </Card>
        ))}
      </div>
    </section>
  );
}

function RelatedSolutions({ segment }) {
  return (
    <section className="solution-related" aria-labelledby="solution-related-title">
      <div>
        <p>Choose the closest operating model</p>
        <h2 id="solution-related-title">Also serving other education providers</h2>
      </div>
      <ul data-testid={`segment-${segment}-cross-links`}>
        {SOLUTION_KEYS.filter((key) => key !== segment).map((key) => {
          const item = SOLUTION_REGISTRY[key];
          return (
            <li key={key}>
              <Link to={`/solutions/${key}`}>
                <item.icon aria-hidden="true" />
                <span><strong>{item.badge}</strong><small>{item.heroLabel}</small></span>
                <ChevronRight aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function SegmentPage() {
  const { segment } = useParams();
  return SOLUTION_KEYS.includes(segment) ? <SegmentContent /> : <MarketingNotFound />;
}

function SegmentContent() {
  const { segment } = useParams();
  const location = useLocation();
  const solution = solutionFor(segment);
  const marketingContent = useMarketingContent();
  const entries = Array.isArray(marketingContent?.segments?.entries)
    ? marketingContent.segments.entries
    : [];
  const cms = entries.find((entry) => entry?.id === segment) || {};
  const badge = typeof cms.badge === "string" && cms.badge.trim() ? cms.badge : solution.badge;
  const headline = typeof cms.headline === "string" && cms.headline.trim() ? cms.headline : solution.headline;
  const subhead = typeof cms.subhead === "string" && cms.subhead.trim() ? cms.subhead : solution.subhead;
  const Icon = solution.icon;

  React.useEffect(() => { initAnalytics(); }, []);
  React.useEffect(() => {
    const hashId = location.hash.replace(/^#/, "");
    if (!hashId) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(hashId);
      target?.scrollIntoView?.({ block: "start", behavior: "auto" });
      target?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [segment, location.hash]);

  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://vidyapeeth360.com/" },
      { "@type": "ListItem", position: 2, name: "Solutions", item: "https://vidyapeeth360.com/solutions" },
      { "@type": "ListItem", position: 3, name: solution.badge, item: `https://vidyapeeth360.com/solutions/${segment}` },
    ],
  };
  const faqSchema = solutionFaqSchema(solution);

  return (
    <MarketingMotion>
      <div className="min-h-screen bg-surface landing-page solution-page" data-segment={segment} data-testid={`segment-${segment}`}>
        <MarketingSeo
          title={solution.seo.title}
          description={solution.seo.description}
          path={`/solutions/${segment}`}
          schema={{ "@context": "https://schema.org", "@graph": [breadcrumb, { ...faqSchema, "@context": undefined }] }}
        />
        <MarketingTopBar />
        <main id="main" tabIndex="-1">
          <section className="solution-hero" aria-labelledby="solution-hero-title">
            <div className="solution-hero__ambient" aria-hidden="true" />
            <div className="solution-hero__grid">
              <SolutionStagger as="div" className="solution-hero__copy" play="mount" stagger={0.055} delay={0.04}>
                <SolutionStaggerItem className="solution-hero__badge"><Badge tone="blue"><Icon aria-hidden="true" /> {badge}</Badge></SolutionStaggerItem>
                <SolutionStaggerItem as="p" className="solution-hero__eyebrow">{solution.heroLabel}</SolutionStaggerItem>
                <SolutionStaggerItem as="h1" id="solution-hero-title"><BrandCopy>{headline}</BrandCopy></SolutionStaggerItem>
                <SolutionStaggerItem as="p" className="solution-hero__sub"><BrandCopy>{subhead}</BrandCopy></SolutionStaggerItem>
                <SolutionStaggerItem className="solution-hero__actions">
                  <ButtonLink
                    to="/register"
                    size="lg"
                    onClick={() => track("trial_cta_click", { placement: "segment_hero", segment })}
                  >
                    {CTA_PRIMARY_LABEL} <ArrowRight aria-hidden="true" />
                  </ButtonLink>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={() => {
                      track("segment_sales_click", { placement: "segment_hero", segment });
                      requestBookDemo({ placement: "segment_hero", contextLabel: `${solution.audience} workflows` });
                    }}
                  >
                    Book a demo
                  </Button>
                </SolutionStaggerItem>
                <SolutionStaggerItem as="ul" className="solution-hero__assurance" aria-label="Trial assurances">
                  <li><Check aria-hidden="true" /> 90-day trial</li>
                  <li><Check aria-hidden="true" /> No card</li>
                  <li><Check aria-hidden="true" /> Fit review included</li>
                </SolutionStaggerItem>
              </SolutionStagger>
              <SolutionReveal className="solution-hero__media-reveal" variant="scale" play="mount">
                <SolutionHeroMedia media={solution.media} priority />
              </SolutionReveal>
            </div>
          </section>

          <section className="solution-proof-band" aria-label={`What is included for ${solution.audience}`}>
            <ul>
              {solution.outcomes.map(([value, label]) => (
                <li key={label}><strong>{value}</strong><span>{label}</span></li>
              ))}
            </ul>
          </section>

          <SolutionSectionNav />
          <SolutionProductShowcase segment={segment} />
          <div className="solution-page__body">
            <SolutionWorkflowSection solution={solution} />
            <SolutionCapabilitySection solution={solution} />
            <SolutionFitSection solution={solution} />
            <SolutionRolloutSection solution={solution} />
            <SolutionFaqSection solution={solution} />
          </div>

          <RelatedSolutions segment={segment} />

          <section className="solution-closing" aria-labelledby="solution-closing-title">
            <div>
              <p>Start with evidence</p>
              <h2 id="solution-closing-title">Ready to review the fit for your {solution.noun}?</h2>
              <p>Use the trial and a workflow review to confirm scope, providers, data migration and plan availability before a wider rollout.</p>
              <div>
                <ButtonLink
                  to="/register"
                  size="lg"
                  className="solution-closing__primary"
                  onClick={() => track("trial_cta_click", { placement: "segment_close", segment })}
                >
                  {CTA_PRIMARY_LABEL} <ArrowRight aria-hidden="true" />
                </ButtonLink>
                <Button
                  size="lg"
                  variant="ghost"
                  className="solution-closing__secondary"
                  onClick={() => {
                    track("segment_sales_click", { placement: "segment_close", segment });
                    requestBookDemo({ placement: "segment_close", contextLabel: `${solution.audience} workflows` });
                  }}
                >
                  Book a demo
                </Button>
              </div>
            </div>
          </section>
        </main>
        <SiteFooter />
      </div>
    </MarketingMotion>
  );
}

export { SolutionHeroMedia, SolutionSectionNav };
